import { useI18N, useResponsive, useTheme } from "@sundae/react-hooks";
import { AssetAmount } from "@sundaeswap/asset";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
} from "chart.js";
import isEqual from "lodash/isEqual";
import transparentize from "polished/lib/color/transparentize";
import { FC } from "react";
import { Chart } from "react-chartjs-2";
import { useCustomCompareMemo } from "use-custom-compare";
import { VestingParty } from "../../../gql/generated/taste-test.sdk";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { randomColorBetweenWithAddress } from "../../../utils/color.utils";
import { safariSafeDate } from "../../../utils/dates.utils";
import { useVestingScheduleChartOptions } from "./hooks/useVestingScheduleChartOptions";
const {
  theme: { colors },
} = require("@sundae/tailwind-config");

ChartJS.register(
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LogarithmicScale,
  RadialLinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
);

interface IGroupedData {
  [key: string]: {
    address: string;
    label: string;
    data: AssetAmount[];
    dates: string[];
  };
}

const defaultPrimaryColor = colors.primary["DEFAULT"];
const defaultSecondaryColor = colors.secondary["DEFAULT"];

const TasteTestVestingScheduleLineChart: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const responsive = useResponsive();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const chartOptions = useVestingScheduleChartOptions({
    activeTasteTest: tasteTest,
    currentLocale,
    isDark,
    responsive,
    t,
    vestingData: tasteTest?.vestingSchedule,
  });
  const primaryChartColor = tasteTestPersonalization?.primaryColor || defaultPrimaryColor;
  const secondaryChartColor = tasteTestPersonalization?.secondaryColor || defaultSecondaryColor;

  const processData = useCustomCompareMemo(
    () => {
      const groupedData: IGroupedData = {};

      // Ensure tasteTest.vestingSchedule is defined before proceeding
      tasteTest?.vestingSchedule?.forEach((entry: VestingParty) => {
        const { address, amount, releaseDate, label } = entry;
        const safariSafe_releaseDate = safariSafeDate(releaseDate.format);

        if (!groupedData[address]) {
          groupedData[address] = {
            label,
            data: [],
            dates: [],
            address,
          };
        }

        const previousAssetAmount =
          groupedData[address].data.slice(-1)[0] || new AssetAmount("0", tasteTest?.asset);
        const assetAmount = new AssetAmount(amount, tasteTest?.asset);
        groupedData[address].data.push(previousAssetAmount.add(assetAmount));
        groupedData[address].dates.push(
          new Intl.DateTimeFormat(currentLocale, {
            month: "short",
            day: "numeric",
            year: "numeric",
          }).format(new Date(safariSafe_releaseDate || ""))
        );
      });

      const datasets = Object.values(groupedData).map((group, _index) => {
        const color = randomColorBetweenWithAddress(
          group.address,
          primaryChartColor,
          secondaryChartColor
        );

        return {
          label: group.address,
          data: group.dates.map((date, i) => ({
            x: date,
            y: group.data[i].value.toNumber(),
          })),
          fill: true,
          backgroundColor: transparentize(isDark ? 0.8 : 0.7, color),
          borderColor: color,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 8,
          pointBackgroundColor: color,
        };
      });

      // Use the first dataset to define the labels
      const labels = groupedData[Object.keys(groupedData)[0]]?.dates?.map((date) => date);

      return { labels, datasets };
    },
    [tasteTest?.vestingSchedule ?? []],
    isEqual
  );

  return <Chart type="line" data={processData} options={chartOptions} />;
};

export default TasteTestVestingScheduleLineChart;
